import React, { useEffect, useState } from 'react';
import { fetchItems } from '../../../../../hooks/useCrud';
import { usePagination } from '../../../../../hooks/usePagination';
import Pagination from '../../../../../components/Pagination';
import LoaderAndError from '../../../../../components/LoaderAndError';
import { fileUrl } from '../../../../../api/config';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { formatDateTimeFR, truncateText } from '../../../../../api/callApi';

interface UiPhoto {
    id?: number;
    icone?: string;
    logoFile?: File;
    createdAt?: string;
    updatedAt?: string;
}

export default function Galery() {
    const [listData, setDataList] = useState<UiPhoto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(12);
    const [totalPages, setTotalPages] = useState(1);

    const [isOpen, setIsOpen] = useState(false);
    const [photoIndex, setPhotoIndex] = useState(0);

    const { paginationRange, isCurrentPage, isFirstPage, isLastPage } = usePagination({
        currentPage,
        totalPages,
    });

    const loadlistData = async () => {
        setLoading(true);
        try {
            const res = await fetchItems<UiPhoto>('/fetch_galery_data', {
                q: search,
                page: currentPage,
                limit,
            });
            setDataList(res.data);
            setTotalPages(res.totalPages);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadlistData();
    }, [search, currentPage, limit]);

    const images = listData.map(item => fileUrl + '/images/' + item.icone);

    // Reset photoIndex quand listData change
    useEffect(() => {
        document.title = "Explorez nos moments forts en images - Swiftride";
        setPhotoIndex(0);
    }, [listData]);

    const handleClickImage = (index: number) => {
        setPhotoIndex(index);
        setIsOpen(true);
    };

    // DEBUG URLs
    // console.log('Images:', images);

    return (
        <section className="py-5" id="galerie">
            {/* HEADER */}
            <div className="text-center mb-5">
                <h2 className="display-10 fw-bold">
                    Moments <span className="text-primary">Immortalisés</span>
                </h2>
                <p className="text-muted lead">
                    Découvrez les performances, émotions et temps forts de l’Ouragan Academy.
                </p>
            </div>

            {/* 🔍 Barre de recherche */}
            <div className="row justify-content-center mb-4">
                <div className="col-lg-12">
                    <div className="input-group shadow-sm rounded-pill overflow-hidden">
                        <input
                            type="text"
                            className="form-control border-0 py-3 px-4"
                            placeholder="Rechercher une image..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                        <button
                            className="btn btn-primary px-4"
                            onClick={() => loadlistData()}
                        >
                            <i className="fas fa-search"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div className="container">
                {listData.length === 0 ? (
                    <LoaderAndError
                        loading={loading}
                        error={error}
                        onClearError={() => setError(null)}
                    />
                ) : (
                    <div className="row g-4">
                        {listData.map((item, index) => (
                            <div
                                key={item.id ?? index}
                                className="col-6 col-md-6 col-lg-4"
                            >
                                <div
                                    className="project-card shadow-sm rounded-4 border-0 h-100 overflow-hidden bg-white dark:bg-gray-900 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 p-0"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleClickImage(index)}
                                >
                                    {/* IMAGE */}
                                    <div className="position-relative">
                                        <img
                                            src={images[index]}
                                            alt={item.icone}
                                            className="img-fluid w-100"
                                            style={{ height: "180px", objectFit: "cover" }}
                                        />

                                        {/* Overlay au survol */}
                                        <div className="position-absolute top-0 start-0 w-100 h-100 
                                        bg-dark bg-opacity-50 d-flex flex-column justify-content-center align-items-center 
                                        opacity-0 hover-opacity transition"
                                        >
                                            <i className="fas fa-search-plus text-white fs-1 mb-2"></i>
                                            <span className="text-white small">
                                                Voir l'image
                                            </span>
                                        </div>

                                        {/* Badge date */}
                                        <span className="badge bg-primary position-absolute top-0 end-0 m-2">
                                            {formatDateTimeFR(item.createdAt ?? "")}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* PAGINATION */}
                <div className="text-center mt-4">
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        paginationRange={paginationRange}
                        isCurrentPage={isCurrentPage}
                        isFirstPage={isFirstPage}
                        isLastPage={isLastPage}
                        onPageChange={setCurrentPage}
                        textCounter={true}
                    />
                </div>
            </div>

            {/* LIGHTBOX */}
            {isOpen && images.length > 0 && (
                <Lightbox
                    mainSrc={images[photoIndex]}
                    nextSrc={images[(photoIndex + 1) % images.length]}
                    prevSrc={images[(photoIndex + images.length - 1) % images.length]}
                    onCloseRequest={() => setIsOpen(false)}
                    onMovePrevRequest={() =>
                        setPhotoIndex((photoIndex + images.length - 1) % images.length)
                    }
                    onMoveNextRequest={() =>
                        setPhotoIndex((photoIndex + 1) % images.length)
                    }
                />
            )}
        </section>
    );
}
